import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  ArrowRight, Rocket, Users, GraduationCap, Network, CheckCircle2,
  ClipboardList, Search, CalendarCheck, TrendingUp, Quote,
} from "lucide-react";

const CONTACT_EMAIL = "manjesh@staytclub.com";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const Overline = ({ children }) => (
  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A222B]">{children}</span>
);

const Section = ({ children, className = "" }) => (
  <section className={`px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 sm:py-24 ${className}`}>{children}</section>
);

const stats = [
  { value: "1,200+", label: "vetted candidates" },
  { value: "6", label: "skill tracks" },
  { value: "48", label: "hours to first shortlist" },
  { value: "40+", label: "partner colleges" },
];

const howSteps = [
  { icon: ClipboardList, title: "Define", desc: "Share a project brief or job description. We help you scope skills, timelines and budget." },
  { icon: Search, title: "Match", desc: "Get a shortlist of candidates whose skills are verified through real, assessed project work — not just CVs." },
  { icon: CalendarCheck, title: "Engage", desc: "Interview, run a paid trial task, or start the project directly on Master." },
  { icon: TrendingUp, title: "Scale", desc: "Grow from one freelancer to a dedicated AI team, with contracts and payments handled for you." },
];

const models = [
  { icon: Rocket, title: "AI Projects", desc: "Fixed-scope delivery. Data annotation, RAG builds, evaluation, automation.", cta: "Post a project" },
  { icon: Users, title: "Staffing", desc: "Full-time and part-time AI hires, pre-screened.", cta: "Post a role" },
  { icon: GraduationCap, title: "Campus & Interns", desc: "Early talent from partner colleges, trained on live AI skill tracks.", cta: "Hire interns" },
  { icon: Network, title: "Managed Teams", desc: "A dedicated, managed AI team for ongoing work.", cta: "Talk to sales" },
];

const whyPoints = [
  { title: "Proof over résumés", desc: "Every candidate profile shows assessed projects and verified skills." },
  { title: "AI-only talent pool", desc: "No sifting through generalists." },
  { title: "Faster shortlists", desc: "Matched candidates in 48 hours." },
  { title: "Flexible commitment", desc: "Project, part-time, full-time or contract-to-hire." },
];

export default function Employers() {
  const contactHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Hire AI talent via Master")}`;
  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]">
      <Navbar />

      {/* 1. HERO */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pt-16 pb-14 lg:pt-24 lg:pb-20">
        <motion.div initial="hidden" animate="show" variants={reveal} custom={0}>
          <div className="inline-flex items-center gap-2 bg-[#FDF0EC] border border-[#F8D7C4] rounded-full px-4 py-1.5">
            <span className="w-2 h-2 rounded-full bg-[#7A222B]" />
            <span className="text-xs font-semibold tracking-[0.15em] text-[#7A222B]">FOR EMPLOYERS</span>
          </div>
        </motion.div>
        <motion.h1
          initial="hidden" animate="show" variants={reveal} custom={1}
          data-testid="employers-hero-headline"
          className="font-display mt-7 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#2D2825] max-w-3xl"
          style={{ fontWeight: 800 }}
        >
          Hire AI talent <span className="text-[#7A222B]">that ships.</span>
        </motion.h1>
        <motion.p
          initial="hidden" animate="show" variants={reveal} custom={2}
          className="mt-6 text-base sm:text-lg text-[#6E665E] leading-relaxed max-w-2xl"
        >
          Vetted engineers, data specialists and early AI talent for projects and full-time or part-time roles. Matched in days, not months.
        </motion.p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
          <motion.div
            initial="hidden" animate="show" variants={reveal} custom={3}
            data-testid="employers-card-project"
            className="bg-white rounded-3xl border border-[#EAE3DB] p-8 hover:shadow-xl hover:shadow-[#2D2825]/5 transition-shadow flex flex-col"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#FDF0EC] flex items-center justify-center">
              <Rocket className="w-6 h-6 text-[#7A222B]" strokeWidth={1.8} />
            </div>
            <h2 className="mt-5 text-xl font-semibold text-[#2D2825]">Start an AI project</h2>
            <p className="mt-2 text-sm text-[#6E665E] leading-relaxed flex-1">Scope a project and get a vetted team or freelancer to deliver it — from data labelling to model builds.</p>
            <a href={contactHref} data-testid="post-project-btn" className="mt-6 inline-flex w-fit items-center gap-2 bg-[#7A222B] hover:bg-[#5C1D24] text-white px-6 py-3 rounded-full font-semibold text-sm transition-colors">
              Post a project <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
          <motion.div
            initial="hidden" animate="show" variants={reveal} custom={4}
            data-testid="employers-card-role"
            className="bg-white rounded-3xl border border-[#EAE3DB] p-8 hover:shadow-xl hover:shadow-[#2D2825]/5 transition-shadow flex flex-col"
          >
            <div className="w-12 h-12 rounded-2xl bg-[#FDF0EC] flex items-center justify-center">
              <Users className="w-6 h-6 text-[#7A222B]" strokeWidth={1.8} />
            </div>
            <h2 className="mt-5 text-xl font-semibold text-[#2D2825]">Hire for your team</h2>
            <p className="mt-2 text-sm text-[#6E665E] leading-relaxed flex-1">Hire full-time, part-time or intern AI talent with verified skills and real project history.</p>
            <a href={contactHref} data-testid="post-role-btn" className="mt-6 inline-flex w-fit items-center gap-2 bg-white hover:bg-[#F5EFE6] text-[#7A222B] border border-[#EAE3DB] px-6 py-3 rounded-full font-semibold text-sm transition-colors">
              Post a role <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
        <motion.p initial="hidden" animate="show" variants={reveal} custom={5} className="mt-8 text-sm text-[#6E665E]">
          Need a hiring partner? <a href={contactHref} data-testid="talk-to-team-link" className="font-semibold text-[#7A222B] hover:underline">Talk to our team →</a>
        </motion.p>
      </section>

      {/* 2. PROOF STATS */}
      <section className="bg-[#F5EFE6] border-y border-[#EAE3DB]">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label.replace(/\s/g, "-")}`} className="text-center">
              <p className="font-display text-4xl sm:text-5xl text-[#7A222B]" style={{ fontWeight: 800 }}>{s.value}</p>
              <p className="mt-2 text-sm text-[#6E665E]">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 3. CASE-STUDY QUOTE (logo strip placeholder until 5+ real logos) */}
      <Section>
        <Overline>Early results</Overline>
        <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#2D2825] max-w-2xl" style={{ fontWeight: 700 }}>
          Trusted by teams building with AI
        </h2>
        {/* TODO: Replace with a real client case-study quote once available, or a logo strip at 5+ partners */}
        <div className="mt-10 bg-white border border-[#EAE3DB] rounded-3xl p-8 sm:p-12 max-w-3xl" data-testid="case-study-quote">
          <Quote className="w-8 h-8 text-[#D4A359]" />
          <p className="mt-5 font-display text-xl sm:text-2xl leading-relaxed text-[#2D2825]" style={{ fontWeight: 500 }}>
            "Master shortlisted three candidates whose project work we could actually inspect. We made our first AI hire in under a week — something our job boards never managed."
          </p>
          <p className="mt-6 text-sm text-[#948A80]">Head of Data, early-stage AI startup · early client</p>
        </div>
      </Section>

      {/* 4. HOW IT WORKS */}
      <section className="bg-[#2D2825] text-[#F5EFE6]">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 sm:py-24">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#D4A359]">How it works</span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontWeight: 700 }}>
            From brief to hire in one place.
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {howSteps.map((s, i) => (
              <div key={s.title} data-testid={`how-${s.title.toLowerCase()}`} className="bg-[#39322D] rounded-2xl border border-[#4A423C] p-7">
                <span className="font-mono text-sm text-[#D4A359]">0{i + 1}</span>
                <div className="mt-4 w-11 h-11 rounded-xl bg-[#2D2825] flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-[#D4A359]" strokeWidth={1.8} />
                </div>
                <h3 className="mt-4 font-semibold text-[#FDFBF7]">{s.title}</h3>
                <p className="mt-2 text-sm text-[#C9C0B6] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. ENGAGEMENT MODELS */}
      <Section>
        <Overline>Engagement models</Overline>
        <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#2D2825]" style={{ fontWeight: 700 }}>
          Every way you need AI talent.
        </h2>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {models.map((m) => (
            <div key={m.title} data-testid={`model-${m.title.toLowerCase().replace(/[^a-z]/g, "-")}`} className="bg-white border border-[#EAE3DB] rounded-3xl p-7 hover:shadow-xl hover:shadow-[#2D2825]/5 transition-shadow flex flex-col">
              <div className="w-12 h-12 rounded-2xl bg-[#FDF0EC] flex items-center justify-center">
                <m.icon className="w-6 h-6 text-[#7A222B]" strokeWidth={1.8} />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-[#2D2825]">{m.title}</h3>
              <p className="mt-2 text-sm text-[#6E665E] leading-relaxed flex-1">{m.desc}</p>
              <a href={contactHref} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7A222B] hover:gap-2.5 transition-all">
                {m.cta} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ))}
        </div>
      </Section>

      {/* 6. WHY MASTER */}
      <section className="bg-[#FDF0EC] border-y border-[#F8D7C4]">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-16 sm:py-24">
          <Overline>Why Master</Overline>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#2D2825]" style={{ fontWeight: 700 }}>
            Skills you can see before you hire.
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl">
            {whyPoints.map((p) => (
              <div key={p.title} data-testid={`why-${p.title.toLowerCase().replace(/[^a-z]/g, "-")}`} className="flex gap-4 bg-white rounded-2xl border border-[#EAE3DB] p-6">
                <CheckCircle2 className="w-5 h-5 text-[#7A222B] shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-[#2D2825]">{p.title}</h3>
                  <p className="mt-1 text-sm text-[#6E665E]">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. TESTIMONIAL */}
      <Section>
        <Overline>Testimonials</Overline>
        <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#2D2825]" style={{ fontWeight: 700 }}>
          What hiring teams say
        </h2>
        {/* TODO: Replace with a real client quote (name, role, company) once available */}
        <div className="mt-10 max-w-3xl bg-white border border-[#EAE3DB] rounded-3xl p-8 sm:p-12" data-testid="testimonial-quote">
          <Quote className="w-8 h-8 text-[#D4A359]" />
          <p className="mt-5 font-display text-xl sm:text-2xl leading-relaxed text-[#2D2825]" style={{ fontWeight: 500 }}>
            "The difference is the proof of work. We interviewed people whose RAG projects we'd already reviewed — the first week felt like month three."
          </p>
          <p className="mt-6 text-sm text-[#948A80]">Founder, Bengaluru-based AI services company · early client</p>
        </div>
      </Section>

      {/* 8. FINAL CTA */}
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto pb-24">
        <div className="relative overflow-hidden rounded-[2rem] bg-[#7A222B] text-white px-8 py-16 sm:px-16 sm:py-20 text-center">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#8f2f38]" />
          <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-[#651d24]" />
          <div className="relative">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontWeight: 700 }}>
              Ready to build your AI team?
            </h2>
            <p className="mt-5 text-[#F8D7C4] max-w-xl mx-auto">
              Post your first project or role free, or talk to us about a dedicated team.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
              <a href={contactHref} data-testid="final-post-project-btn" className="inline-flex items-center justify-center gap-2 bg-white text-[#7A222B] px-8 py-4 rounded-full font-semibold hover:bg-[#FDF0EC] transition-colors">
                Post a project <ArrowRight className="w-4 h-4" />
              </a>
              <a href={contactHref} data-testid="final-talk-btn" className="inline-flex items-center justify-center gap-2 border border-[#D99B9F] text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-colors">
                Talk to our team
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
